import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimatedCodeBackground from '../../components/base/AnimatedCodeBackground';

interface VideoItem {
  url: string;
  title: string;
  description: string;
  uploadDate: string;
  likes?: number;
}

export default function Gallery() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [likedVideos, setLikedVideos] = useState<Set<number>>(new Set());

  // SEO: 動態設置 Gallery 頁面的 meta 標籤與 JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const pageUrl = `${siteUrl}/gallery`;

    // 更新 title
    const prevTitle = document.title;
    document.title = "Mad Little Potato's Art Gallery | Digital Art & Creative Collection";

    // 更新 description
    let descMeta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const prevDesc = descMeta?.content || '';
    if (descMeta) {
      descMeta.content = "Explore Mad Little Potato's creative art gallery — a colorful collection of digital art, blockchain-inspired videos, and Potato Doodles NFTs from the crazy potato universe.";
    }

    // 更新 canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = canonical?.href || '';
    if (canonical) canonical.href = pageUrl;

    // 更新 og:url
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
    if (ogUrl) ogUrl.content = pageUrl;

    // 更新 og:title
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    if (ogTitle) ogTitle.content = "Mad Little Potato's Art Gallery | Digital Art & Creative Collection";

    // 更新 og:description
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
    if (ogDesc) ogDesc.content = "Explore Mad Little Potato's creative art gallery — a colorful collection of digital art, blockchain-inspired videos, and Potato Doodles NFTs.";

    // 注入 CollectionPage JSON-LD
    const collectionSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: "Mad Little Potato's Art Gallery",
      description: "A colorful collection of digital art, blockchain-inspired videos, and Potato Doodles NFTs from the Mad Little Potato universe.",
      url: pageUrl,
      image: 'https://static.readdy.ai/image/b337e33e944c7808b35c7fd3b1e8177a/a58133f4e19f46b66a01cddc395abf43.jpeg',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Mad Little Potato',
        url: siteUrl
      },
      author: {
        '@type': 'Person',
        name: 'Mad Little Potato',
        url: siteUrl
      }
    };

    let schemaEl = document.getElementById('schema-gallery');
    if (!schemaEl) {
      schemaEl = document.createElement('script');
      schemaEl.id = 'schema-gallery';
      (schemaEl as HTMLScriptElement).type = 'application/ld+json';
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify(collectionSchema);

    return () => {
      document.title = prevTitle;
      if (descMeta) descMeta.content = prevDesc;
      if (canonical) canonical.href = prevCanonical;
      if (ogUrl) ogUrl.content = prevCanonical;
      if (ogTitle) ogTitle.content = 'Mad Little Potato - 3-Year-Old Blockchain Character | Digital Art & Crypto Adventures';
      if (ogDesc) ogDesc.content = 'Meet Mad Little Potato, a creative 3-year-old character living in the blockchain world with crazy DNA and endless imagination.';
      const el = document.getElementById('schema-gallery');
      if (el) el.remove();
    };
  }, []);

  // 從雲端載入作品
  useEffect(() => {
    loadGalleryVideos();
    // 從本地存儲載入按讚狀態
    const savedLikes = localStorage.getItem('madpotato_liked_videos');
    if (savedLikes) {
      setLikedVideos(new Set(JSON.parse(savedLikes)));
    }
  }, []);

  // 載入畫廊影片
  const loadGalleryVideos = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-gallery-videos`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // 轉換為新的格式，如果是舊格式的話
          const formattedVideos = data.videos.map((video: any, index: number) => {
            if (typeof video === 'string') {
              return {
                url: video,
                title: `Creation #${index + 1}`,
                description: 'Created with love by Mad Little Potato 🥔✨',
                uploadDate: new Date().toISOString(),
                likes: Math.floor(Math.random() * 50) + 10 // 隨機初始按讚數
              };
            }
            return {
              ...video,
              likes: video.likes || Math.floor(Math.random() * 50) + 10
            };
          });
          setVideos(formattedVideos);
        }
      }
    } catch (error) {
      console.error('載入作品失敗:', error);
    }
  };

  // 處理按讚功能
  const handleLike = (index: number) => {
    const newLikedVideos = new Set(likedVideos);
    const newVideos = [...videos];
    
    if (likedVideos.has(index)) {
      // 取消按讚
      newLikedVideos.delete(index);
      newVideos[index].likes = (newVideos[index].likes || 0) - 1;
    } else {
      // 按讚
      newLikedVideos.add(index);
      newVideos[index].likes = (newVideos[index].likes || 0) + 1;
    }
    
    setLikedVideos(newLikedVideos);
    setVideos(newVideos);
    
    // 保存到本地存儲
    localStorage.setItem('madpotato_liked_videos', JSON.stringify([...newLikedVideos]));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-yellow-100">
      {/* 活潑程式碼背景 */}
      <AnimatedCodeBackground opacity={0.1} />

      {/* Header */}
      <header className="relative z-10 py-8 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-2xl"></i>
            <span className="text-lg font-semibold">Back to Home</span>
          </button>
          
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-600 font-['Pacifico',serif]">
            🎨 Mad Little Potato's Art Gallery
          </h1>
          
          <div className="w-32"></div> {/* Spacer for centering */}
        </div>
      </header>

      {/* Gallery Section */}
      <section className="py-12 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-green-600 font-['Pacifico',serif]">
            🌈 My Creative Collection ({videos.length})
          </h2>

          {videos.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-8xl mb-6">🎭</div>
              <h3 className="text-2xl font-bold text-gray-700 mb-4">No Creations Yet</h3>
              <p className="text-gray-600 text-lg">
                The creative collection is coming soon! Stay tuned for amazing creations!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video, index) => (
                <div key={index} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-pink-200/50">
                  <div className="relative">
                    <video
                      src={video.url}
                      controls
                      autoPlay
                      muted
                      className="w-full aspect-video object-cover"
                      preload="metadata"
                      playsInline
                      controlsList="nodownload"
                    >
                      Your browser does not support video playback
                    </video>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-bold text-gray-800 mb-2">{video.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {video.description}
                    </p>
                    
                    {/* 按讚區域 */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleLike(index)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 cursor-pointer ${
                          likedVideos.has(index)
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <i className={`${likedVideos.has(index) ? 'ri-heart-fill' : 'ri-heart-line'} text-xl`}></i>
                        <span className="font-semibold">{video.likes || 0}</span>
                      </button>
                      
                      <div className="text-gray-400 text-xs">
                        💖 {likedVideos.has(index) ? 'You liked this!' : 'Tap to like'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Floating Decorations */}
      <div className="fixed top-20 left-10 w-16 h-16 bg-yellow-300 rounded-full animate-bounce opacity-60 pointer-events-none"></div>
      <div className="fixed top-40 right-20 w-12 h-12 bg-pink-300 rounded-full animate-pulse opacity-50 pointer-events-none"></div>
      <div className="fixed bottom-32 left-20 w-20 h-20 bg-purple-300 rounded-full animate-bounce delay-300 opacity-40 pointer-events-none"></div>
      <div className="fixed bottom-20 right-32 w-14 h-14 bg-blue-300 rounded-full animate-pulse delay-500 opacity-50 pointer-events-none"></div>

      {/* Stars */}
      <div className="fixed top-32 left-1/4 text-3xl text-yellow-400 animate-pulse pointer-events-none">⭐</div>
      <div className="fixed top-60 right-1/4 text-2xl text-pink-400 animate-bounce pointer-events-none">✨</div>
      <div className="fixed bottom-40 left-1/3 text-4xl text-purple-400 animate-pulse delay-700 pointer-events-none">🌟</div>
      <div className="fixed bottom-60 right-1/3 text-2xl text-green-400 animate-bounce delay-900 pointer-events-none">💫</div>
    </div>
  );
}
